import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter } from '@angular/core';
import { SuraIconComponent } from '../../../atoms/icon/icon.component';

export interface OptionItemConfig {
  id: string;
  title: string;
  subtitle?: string;
  icon: string;
  iconPosition?: 'left' | 'right';
  onlyIcon?: boolean;
  active?: boolean;
  clickable?: boolean;
  separator?: boolean;
}

/**
 * Componente OptionItem simplificado - Solo UI, sin arquitectura hexagonal
 */
@Component({
  selector: 'sura-option-item',
  standalone: true,
  imports: [CommonModule, SuraIconComponent],
  template: `
    @if (config.separator) {
      <div class="h-px bg-gray-200 dark:bg-gray-700 my-2 mx-3"></div>
    }
    @if (!config.separator || config.title) {
      <div 
        [class]="containerClasses"
        [style.cursor]="config.clickable ? 'pointer' : 'default'"
        (click)="handleClick()"
      >
        <div class="flex items-center w-full group"
             [ngClass]="{
               'py-2.5 px-3': !config.onlyIcon,
               'py-2.5 px-2 justify-center': config.onlyIcon
             }">
          <div class="flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg transition-colors duration-200"
               [ngClass]="{
                 'text-white': config.active,
                 'text-sura-gray-800 dark:text-gray-300 group-hover:text-sura-blue-500 dark:group-hover:text-sura-blue-300': !config.active
               }">
            <sura-icon 
              [config]="getIconConfig()"
              class="w-5 h-5">
            </sura-icon>
          </div>
          <div
            class="flex-1 min-w-0 transition-all duration-200"
            [ngClass]="{ 
              'opacity-0 translate-x-2 overflow-hidden w-0 ml-0 pointer-events-none': config.onlyIcon,
              'opacity-100 translate-x-0 ml-3': !config.onlyIcon
            }"
            [style.display]="config.onlyIcon ? 'none' : 'block'"
          >
            <p class="text-sm font-medium truncate leading-tight transition-colors duration-200"
               [ngClass]="{
                 'text-white font-semibold': config.active,
                 'text-gray-800 dark:text-gray-200 group-hover:text-sura-blue-500 dark:group-hover:text-sura-blue-300': !config.active
               }">
              {{ config.title }}
            </p>
            @if (config.subtitle && !config.onlyIcon) {
              <p class="text-xs truncate mt-0.5 transition-colors duration-200"
                 [ngClass]="{
                   'text-sura-blue-200': config.active,
                   'text-sura-gray-200 dark:text-gray-500 group-hover:text-sura-blue-400': !config.active
                 }">
                {{ config.subtitle }}
              </p>
            }
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SuraOptionItemComponent {
  
  @Input({ required: true }) config!: OptionItemConfig;
  @Output() itemClick = new EventEmitter<OptionItemConfig>();

  get containerClasses(): string {
    const classes = ['w-full', 'transition-all', 'duration-200', 'rounded-lg', 'mx-1'];
    
    if (this.config.active) {
      classes.push('bg-sura-blue-500', 'text-white', 'shadow-sm');
    } else {
      classes.push('hover:bg-sura-blue-50', 'dark:hover:bg-gray-700', 'text-gray-700', 'dark:text-gray-200');
    }
    
    if (this.config.clickable) {
      classes.push('cursor-pointer');
    }
    
    return classes.join(' ');
  }

  getIconConfig() {
    return {
      name: this.config.icon,
      size: 'medium' as const,
      clickable: false
    };
  }

  handleClick(): void {
    if (this.config.clickable) {
      this.itemClick.emit(this.config);
    }
  }
}